import { Button, Icon } from '@civilgrid/ui/components';
import { cn } from '@civilgrid/ui/utils';
import * as RadioGroupPrimitive from '@radix-ui/react-radio-group';
import type * as React from 'react';

type ValueChipProps = Omit<React.ComponentProps<typeof RadioGroupPrimitive.Item>, 'children' | 'asChild'> & {
  /** Whether this chip is the field's current choice; picks the `selected` Button variant. */
  checked: boolean;
  /** The visible value text. */
  label: React.ReactNode;
  /** Shows the value in the muted "empty" style. */
  empty?: boolean;
  /** Visible holder count badge, e.g. "2/3"; hidden from the accessibility tree. */
  count?: string;
  /** The dashed "Custom…" chip. */
  custom?: boolean;
};

/** A merge field's radio group of value chips: arrow keys move the choice, Tab leaves the group. */
export function ValueChipGroup({ className, ...props }: React.ComponentProps<typeof RadioGroupPrimitive.Root>) {
  return (
    <RadioGroupPrimitive.Root
      data-slot="merge-value-chip-group"
      loop
      className={cn('flex flex-wrap gap-2', className)}
      {...props}
    />
  );
}

/** One choice in a merge field's radio group: a value with its holder count, or "Custom…". */
export function ValueChip({
  checked,
  label,
  empty = false,
  count,
  custom = false,
  className,
  ...props
}: ValueChipProps) {
  return (
    <RadioGroupPrimitive.Item asChild {...props}>
      <Button
        variant={checked ? 'selected' : 'secondary'}
        size="standard"
        className={cn(
          'min-h-8 max-w-full justify-start gap-2 px-2.5 text-left text-sm font-normal whitespace-normal',
          !checked && 'border-grey-60 bg-white hover:border-blue-50 hover:bg-white',
          custom && 'border-dashed',
          className,
        )}
      >
        {checked && <Icon icon="check" size={14} />}
        <span className={cn('line-clamp-3 wrap-anywhere', empty && 'italic text-grey-70')}>{label}</span>
        {count && (
          <span
            aria-hidden="true"
            className="shrink-0 rounded-full bg-grey-10 px-1.5 py-0.5 font-noto-mono text-xs leading-none whitespace-nowrap text-grey-90"
          >
            {count}
          </span>
        )}
      </Button>
    </RadioGroupPrimitive.Item>
  );
}
